import React, { FC, ReactElement, useEffect, useRef, useState } from 'react'
import { Select, Form, Input, Button, Checkbox, Space, Tooltip, message } from 'antd'
import type { TextAreaRef } from 'antd/lib/input/TextArea'
import { CommentFormProps, CommentFormValues } from './types'
import { UserRole } from 'models/user/user'
import CommentFormMessages from './CommentForm.messages'
import { useMutation } from '@tanstack/react-query'
import { set } from 'lodash'
import axios from 'axios'
import { CommentShared } from 'models/comment/comment'
import { Requests, ReactQuery } from '../../../api'
import { useAnchoredDraftActions } from '../../../hooks/useAnchoredDraftActions'
import { handoffStore } from '../../../utils/aiAnalysis/handoffStore'
import { AI_SECTION_EVENTS, draftAgeSeconds, trackSectionEvent } from '../../../utils/aiAnalysis/rumFunnel'
import { normalizeDraftText } from '../../../utils/aiAnalysis/normalizeDraftText'
import { getRecipientBucket } from '../../AiAnalysis/AiDraftPopover/Editor'
import { RECIPIENT_LABELS } from '../../../models/aiAnalysis/workflow'
import type { DraftAction } from '../../../models/aiAnalysis/workflow'
import '../../Shared/Ant/App.less'

const RECIPIENT_VALUES_BY_BUCKET: { [bucket: string]: CommentShared } = {
  [RECIPIENT_LABELS.NOTARY]: CommentShared.WithAgent,
  [RECIPIENT_LABELS.CLIENT]: CommentShared.WithClient,
  [RECIPIENT_LABELS.CONSUMER]: CommentShared.WithConsumer,
  [RECIPIENT_LABELS.SCHEDULER]: CommentShared.WithCompany
}

const useHeldDraft = (): DraftAction | null => {
  const [heldDraft, setHeldDraft] = useState<DraftAction | null>(handoffStore.getHeld)

  useEffect(() => handoffStore.subscribe(() => setHeldDraft(handoffStore.getHeld())), [])

  return heldDraft
}

export const CommentForm: FC<CommentFormProps> = ({
  commentRecipients,
  userRole,
  showNotarySmsText,
  toFieldHelpText,
  toFieldSelectText,
  textFieldPlaceHolder,
  orderCommentPath,
  csrfToken,
  orderHasParticipants,
  canNotifyParticipants
}): ReactElement => {
  const [form] = Form.useForm()
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [showToFieldHelpText, setShowToFieldHelpText] = useState(false)
  const [showSendEmailNotificationFieldHelpText, setShowSendEmailNotificationFieldHelpText] = useState(false)
  const preTakeTextRef = useRef('')
  const insertedDraftRef = useRef('')
  const textAreaRef = useRef<TextAreaRef>(null)
  const pendingDraftActions = useAnchoredDraftActions()
  const storeHeldDraft = useHeldDraft()
  const aiSuggestionsEligible = userRole === UserRole.Scheduler
  const heldDraft = aiSuggestionsEligible ? storeHeldDraft : null
  const pendingUndecided = aiSuggestionsEligible
    ? pendingDraftActions.filter((action) => !handoffStore.isDecided(action.id))
    : []
  const suggestionsLocked = pendingUndecided.length > 0 && !heldDraft
  const mutation = useMutation((newComment) => {
    return axios.post(orderCommentPath, newComment, {
      headers: {
        'X-CSRF-Token': csrfToken
      }
    })
  })

  const releaseHeldDraft = (reason: 'text_cleared' | 'recipients_cleared' | 'superseded') => {
    const held = handoffStore.getHeld()
    if (!held) return

    trackSectionEvent(AI_SECTION_EVENTS.RELEASE, { action_id: held.id, reason })
    handoffStore.release()
  }

  const onFieldsChange = () => {
    handleSetShowToFieldHelpText()

    if (showNotarySmsText && form.getFieldValue('to').includes(CommentShared.WithAgent)) {
      setShowSendEmailNotificationFieldHelpText(true)
    } else {
      setShowSendEmailNotificationFieldHelpText(false)
    }
  }

  useEffect(() => {
    if (suggestionsLocked) {
      form.setFieldsValue({ to: [] })
      onFieldsChange()
    }
  }, [suggestionsLocked, form])

  useEffect(() => {
    if (heldDraft && !pendingDraftActions.some((action) => action.id === heldDraft.id)) {
      form.setFieldsValue({ to: [] })
      releaseHeldDraft('superseded')
      onFieldsChange()
    }
  }, [pendingDraftActions, heldDraft, form])

  useEffect(() => {
    if (!heldDraft || !handoffStore.consumePendingInsert()) return

    const recipientValue = RECIPIENT_VALUES_BY_BUCKET[getRecipientBucket(heldDraft)]
    form.setFieldsValue({ to: recipientValue ? [recipientValue] : [] })

    preTakeTextRef.current = form.getFieldValue('text') || ''
    insertedDraftRef.current = heldDraft.action_payload.text
    form.setFieldsValue({ text: heldDraft.action_payload.text })
    onFieldsChange()
    textAreaRef.current?.resizableTextArea?.textArea?.focus()
  }, [heldDraft, form])

  const handleSetShowToFieldHelpText = () => {
    if (
      userRole === UserRole.Client &&
      (form.getFieldValue('to').includes(CommentShared.WithCompany) ||
        form.getFieldValue('to').includes(CommentShared.WithAgent) ||
        form.getFieldValue('to').includes(CommentShared.WithConsumer))
    ) {
      setShowToFieldHelpText(true)
    } else if (
      (userRole === UserRole.Notary || userRole === UserRole.Scheduler) &&
      form.getFieldValue('to').includes(CommentShared.WithClient)
    ) {
      setShowToFieldHelpText(true)
    } else if (userRole === UserRole.Observer && form.getFieldValue('to').length > 0) {
      setShowToFieldHelpText(true)
    } else {
      setShowToFieldHelpText(false)
    }
  }

  const showSendEmailNotificationCheckbox = () => {
    return userRole === UserRole.Scheduler
  }

  // Ordered by the recipient list, so the same selection always reads the same way.
  const recipientReadout = (recipients: string[]) => {
    const labels = commentRecipients
      .filter((recipient) => recipients.includes(recipient.value))
      .map((recipient) => recipient.label)
      .join(', ')

    if (labels === '') return null

    return (
      <>
        {': '}
        <span data-test="recipient-readout">{labels}</span>
      </>
    )
  }

  // Participants are emailed only on a comment shared with the client, so the choice is
  // meaningless until the client is a recipient.
  const showParticipantCheckbox = (recipients: string[]) =>
    showSendEmailNotificationCheckbox() && !!canNotifyParticipants && recipients.includes(CommentShared.WithClient)

  // Notifying participants is part of notifying the comment's recipients, not a choice
  // that outlives it.
  const onSendEmailNotificationChange = (event: any) => {
    if (!event.target.checked) form.setFieldsValue({ notifyParticipants: false })
  }

  const onFinish = (values: CommentFormValues) => {
    if (handoffStore.getHeld()) return

    setSubmitButtonDisabled(true)
    setCreateError(null)

    const params = {
      order_comment: {
        send_email_notification: values.sendEmailNotification,
        text: values.text
      }
    }

    if (showParticipantCheckbox(values.to)) {
      set(params, ['order_comment', 'send_email_notification_to_observers'], !!values.notifyParticipants)
    }

    values.to.forEach((recipient) => set(params, ['order_comment', recipient], true))

    // @ts-ignore
    mutation.mutate(params, {
      onSuccess: onSuccess,
      onError: onError
    })
  }

  // A failed create rejects, so onSuccess never runs and the text survives on its own.
  const onError = (error: any) => {
    setSubmitButtonDisabled(false)
    setCreateError(firstValidationMessage(error) || CommentFormMessages.CREATE.ERROR)
  }

  const firstValidationMessage = (error: any) => {
    const errors = error?.response?.data?.errors
    if (!errors) return null

    const messages = Object.values(errors) as string[][]
    const populated = messages.find((list) => Array.isArray(list) && list.length > 0)

    return populated ? populated[0] : null
  }

  const onSuccess = (response: any) => {
    form.resetFields()
    setSubmitButtonDisabled(false)
    setShowToFieldHelpText(false)
    setShowSendEmailNotificationFieldHelpText(false)
    document.dispatchEvent(new CustomEvent('newOrderCommentAdded', { detail: response.data }))
  }

  const resolveDecision = (actionId: string, { clearText }: { clearText: boolean }) => {
    const wasCurrentHold = handoffStore.getHeld()?.id === actionId
    handoffStore.markDecided(actionId)
    if (!wasCurrentHold) return

    form.setFieldsValue(clearText ? { to: [], text: '' } : { to: [] })
    onFieldsChange()
  }

  const decideHeldDraft = async (decision: 'approved' | 'edited' | 'declined', text?: string) => {
    if (!heldDraft) return

    const action = heldDraft
    const actionId = action.id
    setSubmitButtonDisabled(true)
    try {
      const request = Requests.updateAiAction({
        actionId,
        decision,
        ...(text !== undefined && { text })
      })
      await ReactQuery.patchResource(request, request.data)
      resolveDecision(actionId, { clearText: true })
      trackSectionEvent(AI_SECTION_EVENTS.DECIDE, { action_id: actionId, draft_age_s: draftAgeSeconds(action) })
    } catch (error) {
      if (error?.response?.status === 422) {
        resolveDecision(actionId, { clearText: false })
      } else {
        console.error('AI suggestion decision failed', error)
        message.error(CommentFormMessages.AI_SUGGESTIONS.DECIDE_ERROR)
      }
    } finally {
      setSubmitButtonDisabled(false)
    }
  }

  const onSendHeldDraft = () => {
    if (!heldDraft) return

    const currentText = form.getFieldValue('text') || ''
    const decision =
      normalizeDraftText(currentText) === normalizeDraftText(heldDraft.action_payload.text) ? 'approved' : 'edited'
    decideHeldDraft(decision, currentText)
  }

  const onDeclineHeldDraft = () => decideHeldDraft('declined')

  const onRecipientsChange = (nextRecipients: string[]) => {
    if (heldDraft && nextRecipients.length === 0) releaseHeldDraft('recipients_cleared')
  }

  const onTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (heldDraft && event.target.value === '') releaseHeldDraft('text_cleared')
  }

  // Native undo desyncs against a controlled textarea; the take boundary is an owned pre-take/draft toggle instead.
  const onTextKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!heldDraft || !event.metaKey || event.key.toLowerCase() !== 'z') return

    const currentText = form.getFieldValue('text') || ''
    if (!event.shiftKey && currentText === insertedDraftRef.current) {
      event.preventDefault()
      form.setFieldsValue({ text: preTakeTextRef.current })
      onFieldsChange()
    } else if (event.shiftKey && currentText === preTakeTextRef.current) {
      event.preventDefault()
      form.setFieldsValue({ text: insertedDraftRef.current })
      onFieldsChange()
    }
  }

  return (
    <Form
      form={form}
      name="form"
      initialValues={{ to: [], text: '', sendEmailNotification: true, notifyParticipants: false }}
      onFieldsChange={onFieldsChange}
      className="comment-form"
      onFinish={onFinish}
      data-testid="order-comment-form"
      data-suggestions-locked={suggestionsLocked}
      data-held-recipient={heldDraft ? getRecipientBucket(heldDraft) : undefined}
    >
      <Form.Item noStyle shouldUpdate>
        <Form.Item
          className="to-field"
          name="to"
          label="To"
          labelCol={{ span: 1 }}
          wrapperCol={{ span: 24 }}
          help={orderHasParticipants && showToFieldHelpText ? toFieldHelpText : undefined}
        >
          <Select
            mode="multiple"
            allowClear
            placeholder={toFieldSelectText}
            // A recipient's label carries the client's own company name.
            options={commentRecipients.map((recipient) => ({ ...recipient, 'data-value': recipient.value }))}
            disabled={suggestionsLocked}
            open={heldDraft ? false : undefined}
            onChange={onRecipientsChange}
            data-testid="order-comment-recipients"
            popupClassName="order-comment-recipients-dropdown"
          />
        </Form.Item>
      </Form.Item>
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue }: any) =>
          !heldDraft &&
          getFieldValue('to').length > 0 && (
            <>
              <Form.Item
                className={
                  showSendEmailNotificationCheckbox()
                    ? 'send-email-notification-field'
                    : 'send-email-notification-field-no-checkbox'
                }
                name="sendEmailNotification"
                wrapperCol={{ span: 23, offset: 1 }}
                valuePropName="checked"
                help={
                  showSendEmailNotificationFieldHelpText ? CommentFormMessages.SEND_EMAIL_NOTIFICATION.HELP : undefined
                }
              >
                {showSendEmailNotificationCheckbox() && (
                  <Checkbox onChange={onSendEmailNotificationChange}>
                    {CommentFormMessages.SEND_EMAIL_NOTIFICATION.CHECKBOX}
                    {recipientReadout(getFieldValue('to'))}
                  </Checkbox>
                )}
              </Form.Item>
              {showParticipantCheckbox(getFieldValue('to')) && getFieldValue('sendEmailNotification') && (
                <Form.Item
                  className="notify-participants-field"
                  name="notifyParticipants"
                  wrapperCol={{ span: 23, offset: 1 }}
                  valuePropName="checked"
                >
                  <Checkbox>{CommentFormMessages.SEND_EMAIL_NOTIFICATION.PARTICIPANTS_CHECKBOX}</Checkbox>
                </Form.Item>
              )}
            </>
          )
        }
      </Form.Item>
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue }: any) => (
          <Form.Item
            name="text"
            className="text-field"
            validateStatus={createError ? 'error' : undefined}
            help={
              createError ||
              (suggestionsLocked && (getFieldValue('text') || '') !== '' ? (
                <span data-testid="suggestions-lock-help">{CommentFormMessages.AI_SUGGESTIONS.LOCK_HELP}</span>
              ) : undefined)
            }
          >
            <Input.TextArea
              ref={textAreaRef}
              placeholder={textFieldPlaceHolder}
              autoSize={{ minRows: 2, maxRows: 10 }}
              maxLength={1500}
              showCount
              onChange={onTextChange}
              onKeyDown={onTextKeyDown}
              data-testid="order-comment-text"
            />
          </Form.Item>
        )}
      </Form.Item>
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue }: any) =>
          !heldDraft &&
          (getFieldValue('to').length > 0 || userRole !== UserRole.Scheduler) && (
            <Form.Item className="submit-field">
              <Button
                type="primary"
                htmlType="submit"
                data-testid="send-comment-button"
                disabled={submitButtonDisabled || form.getFieldValue('text') === '' || getFieldValue('to').length === 0}
              >
                {CommentFormMessages.SUBMIT.SEND}
              </Button>
            </Form.Item>
          )
        }
      </Form.Item>
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue }: any) =>
          !heldDraft &&
          userRole === UserRole.Scheduler &&
          getFieldValue('to').length === 0 && (
            <Form.Item className="submit-field">
              <Tooltip title={CommentFormMessages.SUBMIT.CREATE_NOTE_EXPLANATION} trigger={['hover', 'focus']}>
                <Button
                  type="default"
                  htmlType="submit"
                  data-testid="add-internal-note-button"
                  disabled={submitButtonDisabled || form.getFieldValue('text') === ''}
                >
                  {CommentFormMessages.SUBMIT.CREATE_NOTE}
                </Button>
              </Tooltip>
            </Form.Item>
          )
        }
      </Form.Item>
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue }: any) =>
          heldDraft && (
            <Form.Item className="submit-field">
              <Space>
                <Button
                  onClick={onDeclineHeldDraft}
                  disabled={submitButtonDisabled}
                  data-testid="decline-suggestion-button"
                >
                  {CommentFormMessages.AI_SUGGESTIONS.NO_RESPONSE_NEEDED}
                </Button>
                <Button
                  type="primary"
                  onClick={onSendHeldDraft}
                  disabled={submitButtonDisabled || normalizeDraftText(getFieldValue('text') || '') === ''}
                  data-testid="send-suggestion-button"
                >
                  {CommentFormMessages.SUBMIT.SEND}
                </Button>
              </Space>
            </Form.Item>
          )
        }
      </Form.Item>
    </Form>
  )
}
